<template>
    <div v-if="match.placeholder" class="list-item thumbnail-canvas placeholder-container --placeholder"
         data-category="placeholder">
        <span class="placeholder-preview"></span>
        <span class="placeholder-label"></span>
    </div>

    <div v-else class="match-container" :data-index="match.id" :data-bin-id="match.bin_id"
         :class="{ selected: active }">
        <img :src="thumbnail" @load="revokeObjectURL(thumbnail)" @click="select(match)" :data-index="match.id"/>
        <div class="label-container">
            <v-text-field
                v-if="renamingFile"
                @blur="endFileRenaming"
                @keydown="renameFile"
                v-model="renamingFileName"
                autofocus
                label="File Name"
                dark
                flat
                solo-inverted
                hide-details
            ></v-text-field>
            <div v-else class="label-container-inner">
                <span class="name">{{ nicename(match.name) }}</span>
                <FileTags :file="match"/>
            </div>
        </div>

        <v-menu offset-y :content-class="'file-options-container'">
            <template v-slot:activator="{ on }">
                <i class="material-icons" v-on="on">more_vert</i>
            </template>
            <v-list>
                <v-list-item @click="downloadLUT(match)" :disabled="!canExportLUT || blockLoading">
                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
                    <v-list-item-title>Download a LUT</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadXMP(match)" :disabled="!canExportXMP || blockLoading">
                    <img src="/assets/icons/lr-icon-96x96.png" />
                    <v-list-item-title>Download XMP Profile</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadCO(match)" :disabled="!canExportCO || blockLoading">
                    <img src="/assets/icons/co-icon-96x96.png" />
                    <v-list-item-title>Download Capture One Style</v-list-item-title>
                </v-list-item>
                <v-menu
                    max-width="170"
                    offset-x
                    left
                    :nudge-top="25"
                    open-on-click
                    :content-class="'file-options-container --rating --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on">
                            <i class="material-icons arrow-left">arrow_left</i>
                            <i class="material-icons">star_rate</i>
                            <v-list-item-title>Rating</v-list-item-title>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="ratingTag in tags.rating"
                            :key="ratingTag.value"
                            class="stars-icons-container"
                            :class="{ '--selected': fileHasTag(match, 'rating', ratingTag.value) }"
                            @click="addTag(match, 'rating', ratingTag.value)"
                        >
                            <v-list-item-title v-if="ratingTag.value == 'none'">{{
                                    ratingTag.label
                                }}
                            </v-list-item-title>
                            <i v-else v-for="index in ratingTag.value" :key="index" class="material-icons">star_rate</i>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-menu
                    max-width="170"
                    offset-x
                    left
                    :nudge-top="25"
                    open-on-click
                    :content-class="'file-options-container --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on">
                            <i class="material-icons arrow-left">arrow_left</i>
                            <i class="material-icons">fiber_manual_record</i>
                            <v-list-item-title>Status</v-list-item-title>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="statusTag in tags.status"
                            :key="statusTag.value"
                            :class="{ '--selected': fileHasTag(match, 'status', statusTag.value) }"
                            @click="addTag(match, 'status', statusTag.value)"
                        >
                            <i class="material-icons" :style="{ color: statusTag.styleColor }">fiber_manual_record</i>
                            <v-list-item-title>{{ statusTag.label }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-menu
                    max-width="170"
                    offset-x
                    left
                    :nudge-top="25"
                    open-on-click
                    :content-class="'file-options-container --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on">
                            <i class="material-icons arrow-left">arrow_left</i>
                            <i class="material-icons">label</i>
                            <v-list-item-title>Tag</v-list-item-title>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="colorTag in tags.color"
                            :key="colorTag.value"
                            :class="{ '--selected': fileHasTag(match, 'color', colorTag.value) }"
                            @click="addTag(match, 'color', colorTag.value)"
                        >
                            <i class="material-icons" :style="{ color: colorTag.styleColor }">label</i>
                            <v-list-item-title>{{ colorTag.label }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-list-item @click="downloadImage(match)" :disabled="!canExportLUT || blockLoading">
                    <i class="material-icons">photo</i>
                    <v-list-item-title>Save Image</v-list-item-title>
                </v-list-item>
                <v-list-item @click="applyGrade(match)" :disabled="blockLoading">
                    <i class="material-icons">perm_media</i>
                    <v-list-item-title>Apply Grade</v-list-item-title>
                </v-list-item>
                <v-list-item @click="startFileRenaming(match)" :disabled="!canRenameAsset">
                    <i class="material-icons">create</i>
                    <v-list-item-title>Rename</v-list-item-title>
                </v-list-item>
                <v-list-item @click="remove" :disabled="!canDeleteAsset">
                    <v-icon class="material-icons">mdi-delete</v-icon>
                    <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import download from 'downloadjs';
import {mapActions, mapGetters, mapState} from "vuex";

import CanMixin from "@/mixins/CanMixin";
import FilenameMixin from "@/mixins/Filename";
import {matchesStore} from "@/services/indexedDB.service";
import FileTags from "@/components/app/files/FileTags.vue";

export default {
    mixins: [CanMixin, FilenameMixin],
    components: {
        FileTags
    },

    data() {
        return {
            renamingFile: false,
            renamingFileName: "",
            thumbnail: null,
        };
    },

    props: {
        match: Object,
    },

    created() {
        if (!this.match.placeholder) {
            this.thumbnail = URL.createObjectURL(this.match.blob);
        }
    },

    computed: {
        ...mapState('File/Matches', ['tags']),
        ...mapGetters('Projects', ['assetById']),
        ...mapState('Projects', ['image']),
        ...mapState('App', ['blockLoading']),
        imageHasIDT() {
            // NOTE: fix
            return false;

            // let file = await this.assetById(this.image, 'image');
            //
            // if (file?.IDT && !file.IDT?.initial) return true;
            // else return false;
        },
        active() {
            if (this.$store.state.Projects.match.id == this.match.id) return true;
            else return false;
        },
    },

    methods: {
        ...mapActions("App", ["applyGrade"]),
        ...mapActions("Helpers", ["revokeObjectURL"]),

        nicename(name) {
            return this.stripTimestampAndExtension(name);
        },

        select(match) {
            if (!this.$store.state.Projects.image) return;

            if (!window.lutify.splitViewActive || !this.active) {
                window.lutify.refreshSplitView(match);
            } else {
                window.lutify.removeSplitView();
            }
        },

        async remove() {
            await matchesStore.removeItem(this.match.id);
            this.$store.commit('UI/REMOVE_MATCH', this.match.id);
        },

        fileHasTag(file, tagName, value) {
            if (file.tags) return file.tags.find(tag => tag.name == tagName && tag.value == value);
            else return false;
        },

        async addTag(file, tag_name, tag_value) {
            const selectedFiles = [file];

            if (tag_name == "rating") {
                let sameTagOnAll = selectedFiles
                    .map(e => {
                        if (e.tags.find(tag => tag.name == tag_name)) return e.tags.find(tag => tag.name == tag_name).value;
                    })
                    .every(value => tag_value == value);
                if (sameTagOnAll) {
                    await this.$store.dispatch("File/Matches/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    // NOTE: can be implemented in back-end to auto switch to none
                    await this.$store.dispatch("File/Matches/addTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value: "none",
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            if (tag_name == "color") {
                let sameTagOnAll = selectedFiles
                    .map(e => e.tags.filter(tag => tag.name == "color").map(tag => tag.value))
                    .every(tag => tag.includes(tag_value));

                if (sameTagOnAll) {
                    await this.$store.dispatch("File/Matches/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            await this.$store.dispatch("File/Matches/addTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name,
                tag_value,
            });

            await this.$store.dispatch("Projects/refreshProjectTree");
            // this.refreshSelectedFiles();
        },

        startFileRenaming(file) {
            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename",
                data: file,
            });

            this.renamingFile = file.id;
            this.renamingFileName = this.stripTimestampAndExtension(file.name);
        },

        async renameFile(e) {
            if (e.key == "Enter" || e.keyCode == 13) {
                let oldFilename = this.$store.state.Projects.action.data.name;
                let fileType = oldFilename.substring(oldFilename.lastIndexOf(".") + 1);
                let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

                this.renamingFile = false;

                if (this.stripTimestampAndExtension(oldFilename) !== this.renamingFileName) {
                    await this.$store.dispatch("UI/renameMatch", {
                        id: this.$store.state.Projects.action.data.id,
                        name: newFilename,
                        nicename: this.stripTimestampAndExtension(newFilename),
                        bin_id: this.$store.state.Projects.action.data.bin_id
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree", {
                        createPresignedURL: true,
                    });

                    let matchesStoreData = await matchesStore.getItem(this.$store.state.Projects.action.data.id);
                    if (matchesStoreData)
                        matchesStore.setItem(matchesStoreData.id, {
                            ...matchesStoreData,
                            name: newFilename,
                            nicename: this.renamingFileName
                        });
                }

                this.endFileRenaming();
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endFileRenaming();
            }
        },

        endFileRenaming() {
            this.renamingFile = false;

            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename-end",
                data: null,
            });
        },

        async downloadLUT(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let matchStoreData = await window.lutify.fetchMatch(vm.$store.getters["Projects/matchById"](match.id));
            let matchStoreData = await window.lutify.fetchMatch(match);

            await this.$store.dispatch("Projects/setAction", {
                type: "match",
                action: "export-lut-from-asset",
                data: [matchStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-lut')
        },

        async downloadXMP(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let matchStoreData = await window.lutify.fetchMatch(match);

            await this.$store.dispatch("Projects/setAction", {
                type: "match",
                action: "export-xmp-from-asset",
                data: [matchStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
        },

        async downloadCO(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let matchStoreData = await window.lutify.fetchMatch(match);

            await this.$store.dispatch("Projects/setAction", {
                type: "match",
                action: "export-co-from-asset",
                data: [matchStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
        },

        async downloadImage(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let matchStoreData = await window.lutify.fetchMatch(vm.$store.getters["Projects/matchById"](match.id));
            let matchStoreData = await window.lutify.fetchMatch(match);
            download(this.match.blob, `${matchStoreData.nicename}.jpg`, "image/jpeg");
        },
    },
};
</script>

<style lang="scss">
.match-container {
    display: grid;
    grid-template-areas: "image image" "label delete-btn";
    grid-template-columns: 1fr 20px;
    grid-row-gap: 5px;
    align-items: center;
    align-content: center;

    &:not(:last-of-type) {
        margin-bottom: 20px;
    }

    > img {
        width: 100%;
        grid-area: image;
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: var(--thumbnail-lg-border-radius);

        &:hover {
            border: 2px solid var(--primary);
        }
    }

    &.selected > img {
        border: 2px solid var(--primary);
    }

    > .label-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        grid-area: label;
        overflow: hidden;

        .label-container-inner {
            display: grid;
            align-content: center;
            grid-template-columns: 1fr minmax(0, auto);
            width: 100%;
        }

        .name {
            color: var(--font-default-color);
            font-size: 0.9rem;
            text-align: left;
            line-height: 1.2;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            align-self: center;
            padding-right: 5px;
        }

        > i.loading-spinner {
            margin-left: 10px;
            margin-right: 5px;
        }

        .v-input {
            font-size: 12px;

            .v-input__control {
                min-height: initial;
                // height: 10px;
                input {
                    line-height: 1;
                    color: var(--font-default-color) !important;
                    padding: 0;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                }

                .v-input__slot {
                    padding: 0;
                    background: transparent;
                }
            }

            &--is-focused {
                .v-input__slot {
                    // background: #363636;
                    background: transparent;
                }
            }
        }
    }

    i.material-icons {
        font-size: 18px;
        cursor: pointer;
    }
}
</style>
